import { string, oneOf, bool, func, node } from 'prop-types';
import React, { useRef, useEffect } from 'react';
import { getQaKey as qa } from '../../util/qa';
import useStyles from '../../hooks/useStyles';
import cx from 'classnames';
import { hexToRgb } from '../../util/styles';

Button.propTypes = {
	autoFocus: bool,
	children: node,
	className: string,
	qaKey: string,
	fullWidth: bool,
	icon: func,
	kind: oneOf(['primary', 'text']),
	size: oneOf(['xsmall', 'small', 'medium']),
	tabDisabled: bool,
	disabled: bool,
	loading: bool
};

Button.defaultProps = {
	kind: 'primary',
	size: 'medium',
	tabDisabled: false,
	autoFocus: false,
	qaKey: 'btn'
};

export default function Button({
	children,
	kind,
	size,
	icon: Icon,
	tabDisabled,
	className,
	autoFocus,
	disabled,
	loading,
	qaKey,
	...other
}) {
	const buttonRef = useRef();
	const classes = useStyles(styles);

	useEffect(() => {
		if (autoFocus) buttonRef.current.focus();
	}, [autoFocus]);

	// Interactive elements have a default tabIndex of zero, so we omit the attribute when enabled.
	const accessibility = {
		tabIndex: tabDisabled ? -1 : undefined,
		'aria-hidden': tabDisabled || undefined
	};

	const css = [
		classes.buttonBase,
		classes[kind],
		loading && classes.loading,
		!!Icon && classes.buttonWithIcon,
		classes[size]
	];

	return (
		<button
			type={'button'}
			{...other}
			{...qa(qaKey)}
			{...accessibility}
			css={css}
			className={cx([className, 'truncate', 'full-width'])}
			disabled={loading || disabled}
			ref={buttonRef}>
			{children}
			{Icon && <Icon css={classes.buttonIcon} />}
		</button>
	);
}

function styles({ css, theme: { color, getFocusStyles } }) {
	return {
		buttonIcon: css`
			margin-left: 16px;
			width: 20px;
		`,
		primary: css`
			color: ${color.white};
			background-color: ${color.primary};
			&:focus {
				background-color: rgba(${hexToRgb(color.primary)}, 0.9);
				.focus-visible {
					${getFocusStyles()}
				}
			}
			&:hover:not(:disabled) {
				background-color: rgba(${hexToRgb(color.primary)}, 0.9);
			}
		`,
		text: css`
			color: ${color.primary};
			border: 0;
			width: auto;
			height: auto;
			display: inline;
			box-shadow: none;
			margin: 0;
			padding: 0;

			&:hover:not(:disabled) {
				color: ${color.primaryDark};
			}
		`,
		buttonWithIcon: css`
			display: inline-flex;
			justify-content: space-between;
			align-items: center;
		`,
		loading: css`
			&:disabled {
				cursor: wait;
			}
		`,
		buttonBase: css`
			height: 40px;
			padding: 0 16px;
			opacity: 1;
			appearance: none;
			border: none;
			text-align: center;
			white-space: nowrap;
			max-width: 100%;
			border-radius: 4px;
			background-color: transparent;
			transition: border-color 0.2s, box-shadow 0.2s, color 0.2s, background-color 0.2s, border-width 0.2s;
			color: ${color.text};
			box-shadow: inset 0 0 0 0 ${color.secondary};

			&:disabled {
				opacity: 0.5;
			}
		`,
		medium: css`
			height: 40px;
		`,
		small: css`
			height: 32px;
		`,
		xsmall: css`
			height: 28px;
		`
	};
}
